import { DataAppFileId, HexVersionId } from "@hex/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { memoize } from "lodash";

import { RootState } from "../store";
import { createSelector } from "../utils/index.js";

export type DataAppFileMP = {
  path: string;
  contentsText: string;
  revision: number;
};

export type DataAppFileMPState = {
  hexVersionId: HexVersionId;
  files: Record<string, DataAppFileMP>;
  pathToDataAppFileId: Record<string, DataAppFileId>;
  entryPoint: string | null;
  lastModifiedAt: number | null;
  /**
   * Bumps whenever file contents actually change, from either an AO upsert
   * or a query snapshot that differs from what is stored. Unlike
   * `lastModifiedAt` (which only moves on a server publish), this is the
   * signal the edit panel uses to classify a republish — an AO merge changes
   * files before the publish that trails it, and that trailing publish must
   * not read as a second change.
   */
  filesRevision: number;
  isLoading: boolean;
};

type DataAppFileMPSliceState = Partial<
  Record<HexVersionId, DataAppFileMPState>
>;

const initialState: DataAppFileMPSliceState = {};

const makeFilesMap = (
  files: Record<string, string>,
): Record<string, DataAppFileMP> =>
  Object.fromEntries(
    Object.entries(files).map(([path, contentsText]) => [
      path,
      { path, contentsText, revision: 0 },
    ]),
  );

const filesContentEqual = (
  existing: Record<string, DataAppFileMP>,
  incoming: Record<string, string>,
): boolean => {
  const incomingPaths = Object.keys(incoming);
  if (incomingPaths.length !== Object.keys(existing).length) return false;
  return incomingPaths.every(
    (path) => existing[path]?.contentsText === incoming[path],
  );
};

const dataAppFileMPSlice = createSlice({
  name: "dataAppFileMP",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<{ hexVersionId: HexVersionId }>) {
      const { hexVersionId } = action.payload;
      const existing = state[hexVersionId];
      state[hexVersionId] = {
        hexVersionId,
        files: existing?.files ?? {},
        pathToDataAppFileId: existing?.pathToDataAppFileId ?? {},
        entryPoint: existing?.entryPoint ?? null,
        lastModifiedAt: existing?.lastModifiedAt ?? null,
        filesRevision: existing?.filesRevision ?? 0,
        isLoading: true,
      };
    },
    setFromQuery(
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        files: Record<string, string>;
        fileEntries?: Array<{
          dataAppFileId: DataAppFileId;
          filePath: string;
        }>;
        entryPoint: string;
        lastModifiedAt: number;
      }>,
    ) {
      const { entryPoint, fileEntries, files, hexVersionId, lastModifiedAt } =
        action.payload;
      const existing = state[hexVersionId];
      // Only apply if newer than what we already have (guards against out-of-order
      // and duplicate dispatches when multiple hooks both fire the initial query).
      if (
        existing?.lastModifiedAt != null &&
        lastModifiedAt <= existing.lastModifiedAt
      ) {
        return;
      }
      const previousRevision = existing?.filesRevision ?? 0;
      const contentChanged =
        existing == null || !filesContentEqual(existing.files, files);
      state[hexVersionId] = {
        hexVersionId,
        // Keep the stored map when nothing changed so selectors stay
        // referentially stable across a content-identical publish.
        files:
          existing == null || contentChanged
            ? makeFilesMap(files)
            : existing.files,
        pathToDataAppFileId:
          fileEntries != null
            ? Object.fromEntries(
                fileEntries.map((entry) => [
                  entry.filePath,
                  entry.dataAppFileId,
                ]),
              )
            : (existing?.pathToDataAppFileId ?? {}),
        entryPoint,
        lastModifiedAt,
        filesRevision: contentChanged ? previousRevision + 1 : previousRevision,
        isLoading: false,
      };
    },
    setNotFound(state, action: PayloadAction<{ hexVersionId: HexVersionId }>) {
      const { hexVersionId } = action.payload;
      const existing = state[hexVersionId];
      const hadFiles =
        existing != null && Object.keys(existing.files).length > 0;
      state[hexVersionId] = {
        hexVersionId,
        files: {},
        pathToDataAppFileId: {},
        entryPoint: null,
        lastModifiedAt: null,
        filesRevision: (existing?.filesRevision ?? 0) + (hadFiles ? 1 : 0),
        isLoading: false,
      };
    },
    upsertFile(
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        file: DataAppFileMP;
      }>,
    ) {
      const versionState = state[action.payload.hexVersionId];
      if (versionState == null) {
        return;
      }
      const { file } = action.payload;
      const contentChanged =
        versionState.files[file.path]?.contentsText !== file.contentsText;
      versionState.files[file.path] = file;
      if (contentChanged) {
        versionState.filesRevision += 1;
      }
    },
  },
});

export const dataAppFileMPReducer = dataAppFileMPSlice.reducer;
export const dataAppFileMPActions = dataAppFileMPSlice.actions;

const selectDataAppFileMPState = (state: RootState) => state.dataAppFileMP;

export const dataAppFileMPSelectors = {
  version: memoize((hexVersionId: HexVersionId) =>
    createSelector(selectDataAppFileMPState, (state) => state[hexVersionId]),
  ),

  files: memoize((hexVersionId: HexVersionId) =>
    createSelector(dataAppFileMPSelectors.version(hexVersionId), (vs) => {
      if (vs == null || vs.isLoading) return null;
      // Match dataAppSlice semantics: "confirmed no files" / setNotFound is
      // `null`, not `{}`. Callers like useAppBuilderViewState treat non-null
      // as "a data app exists" and hide the app-type chooser.
      const entries = Object.entries(vs.files);
      if (entries.length === 0) return null;
      return Object.fromEntries(
        entries.map(([path, f]) => [path, f.contentsText]),
      ) as Record<string, string>;
    }),
  ),

  selectFiles: (state: RootState, hexVersionId: HexVersionId) =>
    dataAppFileMPSelectors.files(hexVersionId)(state),

  selectFileExists: (
    state: RootState,
    hexVersionId: HexVersionId,
    path: string,
  ): boolean | null => {
    const vs = state.dataAppFileMP[hexVersionId];
    if (vs == null || vs.isLoading) return null;
    return Object.hasOwn(vs.files, path);
  },

  selectEntryPoint: (
    state: RootState,
    hexVersionId: HexVersionId,
  ): string | null => state.dataAppFileMP[hexVersionId]?.entryPoint ?? null,

  /** Null until the first snapshot lands; see `filesRevision`. */
  selectFilesRevision: (
    state: RootState,
    hexVersionId: HexVersionId,
  ): number | null => {
    const vs = state.dataAppFileMP[hexVersionId];
    if (vs == null || vs.isLoading) return null;
    return vs.filesRevision;
  },

  selectIsLoading: (state: RootState, hexVersionId: HexVersionId): boolean =>
    state.dataAppFileMP[hexVersionId]?.isLoading ?? true,

  selectIsLoaded: (state: RootState, hexVersionId: HexVersionId): boolean => {
    const vs = state.dataAppFileMP[hexVersionId];
    return vs != null && !vs.isLoading;
  },

  file: memoize(
    (hexVersionId: HexVersionId, path: string) =>
      createSelector(
        dataAppFileMPSelectors.version(hexVersionId),
        (versionState) => versionState?.files[path],
      ),
    (hexVersionId, path) => `${hexVersionId}:${path}`,
  ),
  dataAppFileIdToPath: memoize(
    (hexVersionId: HexVersionId, dataAppFileId: DataAppFileId) =>
      createSelector(
        dataAppFileMPSelectors.version(hexVersionId),
        (versionState) => {
          if (versionState == null) {
            return null;
          }
          for (const [path, id] of Object.entries(
            versionState.pathToDataAppFileId,
          )) {
            if (id === dataAppFileId) {
              return path;
            }
          }
          return null;
        },
      ),
    (hexVersionId, dataAppFileId) => `${hexVersionId}:${dataAppFileId}`,
  ),
  pathToDataAppFileId: memoize((hexVersionId: HexVersionId) =>
    createSelector(
      dataAppFileMPSelectors.version(hexVersionId),
      (versionState) => versionState?.pathToDataAppFileId ?? {},
    ),
  ),
};
